// umlaut-check: skip — Variablen-/Funktionsnamen ("naechsteKw", "isoWoche"
// usw.) sind technische Bezeichner; sichtbare Texte tragen Umlaute korrekt.

import React, {useEffect, useId, useMemo, useRef, useState} from "react";
import {Overlay} from "react-bootstrap";

const WOCHEN_VOR = 26;
const WOCHEN_NACH = 52;

/** Montag der Kalenderwoche eines Datums (verändert das Original nicht). */
function montagDerWoche(datum) {
    const d = new Date(datum);
    const wochentag = (d.getDay() + 6) % 7; // Montag=0 ... Sonntag=6
    d.setHours(0, 0, 0, 0);
    d.setDate(d.getDate() - wochentag);
    return d;
}

/**
 * ISO-Kalenderwoche und -Jahr eines Datums nach dem Standardverfahren (über
 * den Donnerstag derselben Woche - der liegt immer im "richtigen" Jahr).
 * Ergibt automatisch nur echte Wochen: KW 53 taucht nur in den Jahren auf,
 * deren letzte Woche tatsächlich eine 53. ist (z. B. 2026), weil hier immer
 * von einem echten Kalendertag aus gerechnet wird statt starr 1..53 zu
 * zählen (Sichtprüfung 1, Befund 7: KW 53/25, 53/27 und 53/28 gab es nicht).
 */
function isoWoche(datum) {
    const montag = montagDerWoche(datum);
    const donnerstag = new Date(montag);
    donnerstag.setDate(montag.getDate() + 3);
    const jahresanfang = new Date(donnerstag.getFullYear(), 0, 1);
    const woche = Math.ceil(((donnerstag.getTime() - jahresanfang.getTime()) / 86400000 + 1) / 7);
    return {jahr: donnerstag.getFullYear(), woche};
}

function kwWert(jahr, woche) {
    return `${jahr}-${String(woche).padStart(2, "0")}`;
}

/** "2026-39" -> "KW 39/26"; ungültige/leere Werte kommen unverändert zurück. */
export function kwLabel(wert) {
    if (!wert) {
        return "";
    }
    const treffer = /^(\d{4})-(\d{1,2})$/.exec(String(wert));
    if (!treffer) {
        return String(wert);
    }
    return `KW ${treffer[2].padStart(2, "0")}/${treffer[1].slice(2)}`;
}

/** Kalenderwoche ("JJJJ-WW") von "heute + wochenVersatz Wochen", Montag-basiert. */
export function kwMitVersatz(wochenVersatz = 0) {
    const datum = new Date();
    datum.setDate(datum.getDate() + wochenVersatz * 7);
    const {jahr, woche} = isoWoche(datum);

    return kwWert(jahr, woche);
}

/** Vorbelegung "nächste Woche" für eine neu angelegte Lieferung (Befund 6). */
export function naechsteKw() {
    return kwMitVersatz(1);
}

/**
 * Erzeugt die auswählbaren Wochen von "heute - WOCHEN_VOR" bis
 * "heute + WOCHEN_NACH" - ausschließlich aus echten Kalendertagen abgeleitet
 * (siehe isoWoche()), keine feste 1..53-Schleife über beliebige Jahre.
 */
function erzeugeWochen() {
    const aktuelle = isoWoche(new Date());
    const liste = [];
    const gesehen = new Set();

    for (let versatz = -WOCHEN_VOR; versatz <= WOCHEN_NACH; versatz += 1) {
        const datum = new Date();
        datum.setDate(datum.getDate() + versatz * 7);
        const {jahr, woche} = isoWoche(datum);
        const wert = kwWert(jahr, woche);
        if (gesehen.has(wert)) {
            continue;
        }
        gesehen.add(wert);
        liste.push({wert, jahr, woche, istAktuelleWoche: jahr === aktuelle.jahr && woche === aktuelle.woche});
    }

    return liste;
}

/**
 * Filtert die Wochenliste nach eingetipptem Text - "40" (jede Woche 40 im
 * Bereich), "40/26" oder "2026-40" (genau diese Woche), sonst Teiltreffer im
 * Label bzw. Rohwert (Sichtprüfung 1, Befund 7: "Tipp-Suche").
 */
function filtereWochen(alle, query) {
    const q = query.trim();
    if (q === "") {
        return alle;
    }

    let jahr = null;
    let woche = null;

    let treffer = /^(\d{4})[-\/](\d{1,2})$/.exec(q);
    if (treffer) {
        jahr = Number(treffer[1]);
        woche = Number(treffer[2]);
    } else {
        treffer = /^(\d{1,2})\/(\d{2})$/.exec(q);
        if (treffer) {
            woche = Number(treffer[1]);
            jahr = 2000 + Number(treffer[2]);
        } else if (/^\d{1,2}$/.test(q)) {
            woche = Number(q);
        }
    }

    if (woche !== null) {
        return alle.filter((w) => w.woche === woche && (jahr === null || w.jahr === jahr));
    }

    const ql = q.toLowerCase();

    return alle.filter((w) => kwLabel(w.wert).toLowerCase().includes(ql) || w.wert.includes(q));
}

/**
 * Eigene Kalenderwochen-Auswahl für den Bereich "Lieferant Cosmos" - kein
 * natives Datumsfeld (KONVENTIONEN.md Abschnitt 4). Wert im Hausformat
 * "JJJJ-WW" (cosmos_lieferungen.kw_wunsch usw., DATENMODELL.md Tabelle 1).
 *
 * Sichtprüfung 1, Befund 7: Ersetzt die frühere feste Liste "Vorjahr bis
 * übernächstes Jahr, je 53 Wochen" (die auch nicht existierende Wochen wie
 * KW 53/25 anbot) durch eine Eingabe-/Vorschlagsliste wie
 * ArtikelSuchfeld.jsx - Bereich −26 bis +52 Wochen um heute, Tipp-Suche
 * ("40", "40/26", "2026-40"), Pfeiltasten/Enter/Esc, aktuelle Woche
 * hervorgehoben. Ein bereits gesetzter Wert außerhalb dieses Bereichs
 * (ältere Lieferung) bleibt trotzdem korrekt beschriftet - der Bereich
 * schränkt nur die Vorschlagsliste ein, nicht die Anzeige.
 *
 * @param {string|null} value - "2026-39" oder null (nicht gesetzt).
 * @param {(value: string|null) => void} onChange
 * @param {boolean} [allowEmpty=true] - Leerauswahl anbieten (nullable Felder).
 */
export default function KwSelect({id, value, onChange, size = "sm", ariaLabel, disabled = false, allowEmpty = true, className}) {
    const generatedId = useId();
    const instanceId = id || `cosmos-kw-${generatedId}`;
    const inputRef = useRef(null);
    // "useMemo" statt "useRef(erzeugeWochen())": Ein useRef-Initialwert wird
    // trotzdem bei JEDEM Render neu ausgewertet (nur das Ergebnis wird
    // verworfen) - bei vielen KwSelect-Zellen in einer Tabelle (LieferPositionenTabelle.jsx)
    // würde das die 79 Wochen bei jedem Tastendruck unnötig neu berechnen.
    const alleWochen = useMemo(() => erzeugeWochen(), []);

    const [query, setQuery] = useState(kwLabel(value));
    const [open, setOpen] = useState(false);
    const [activeIndex, setActiveIndex] = useState(0);

    useEffect(() => {
        setQuery(kwLabel(value));
        // Nur reagieren, wenn sich der Wert VON AUSSEN ändert - nicht bei
        // jedem lokalen Tastendruck (wie ArtikelSuchfeld.jsx).
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value]);

    const optionen = filtereWochen(alleWochen, query);
    const ersterEintragIstLeer = allowEmpty;
    const gesamtzahl = optionen.length + (ersterEintragIstLeer ? 1 : 0);

    const schliessen = () => {
        setOpen(false);
        setQuery(kwLabel(value));
    };

    const uebernehmenWert = (neuerWert) => {
        onChange(neuerWert);
        setQuery(kwLabel(neuerWert));
        setOpen(false);
    };

    const waehleIndex = (index) => {
        if (ersterEintragIstLeer && index === 0) {
            uebernehmenWert(null);
            return;
        }
        const treffer = optionen[index - (ersterEintragIstLeer ? 1 : 0)];
        if (treffer) {
            uebernehmenWert(treffer.wert);
        }
    };

    const handleKeyDown = (e) => {
        if (disabled) {
            return;
        }
        if (e.key === "Escape") {
            e.preventDefault();
            schliessen();
            return;
        }
        if (!open) {
            if (["ArrowDown", "ArrowUp", "Enter"].includes(e.key)) {
                e.preventDefault();
                setActiveIndex(0);
                setOpen(true);
            }
            return;
        }

        switch (e.key) {
            case "ArrowDown":
                e.preventDefault();
                setActiveIndex((i) => Math.min(gesamtzahl - 1, i + 1));
                break;
            case "ArrowUp":
                e.preventDefault();
                setActiveIndex((i) => Math.max(0, i - 1));
                break;
            case "Enter":
                e.preventDefault();
                waehleIndex(activeIndex);
                break;
            default:
                break;
        }
    };

    return (
        <>
            <input
                ref={inputRef}
                id={instanceId}
                type="text"
                className={`form-control${size === "sm" ? " form-control-sm" : ""}${className ? " " + className : ""}`}
                style={{minWidth: 110}}
                value={query}
                disabled={disabled}
                autoComplete="off"
                role="combobox"
                aria-expanded={open}
                aria-autocomplete="list"
                aria-controls={`${instanceId}-liste`}
                aria-label={ariaLabel}
                placeholder={allowEmpty ? "– keine –" : "KW wählen"}
                onFocus={(e) => {
                    setActiveIndex(0);
                    setOpen(true);
                    // Erst NACH dem Fokus selektieren - ein Mausklick würde die
                    // Auswahl sonst gleich wieder auf die Klickposition
                    // zurücksetzen. So ersetzt der erste Tastendruck den
                    // ganzen Text statt ihn zu verlängern.
                    const feld = e.target;
                    window.requestAnimationFrame(() => feld.select());
                }}
                onChange={(e) => {
                    setQuery(e.target.value);
                    setActiveIndex(0);
                    setOpen(true);
                }}
                onBlur={() => window.setTimeout(schliessen, 120)}
                onKeyDown={handleKeyDown}
            />

            <Overlay target={inputRef} show={open} placement="bottom-start" flip rootClose onHide={() => setOpen(false)}>
                {(overlayProps) => (
                    <div
                        {...overlayProps}
                        id={`${instanceId}-liste`}
                        role="listbox"
                        aria-label={ariaLabel}
                        className="bg-white border rounded-1 shadow-sm py-1"
                        style={{...overlayProps.style, minWidth: 150, maxHeight: 260, overflowY: "auto", zIndex: 3000}}
                    >
                        {ersterEintragIstLeer && (
                            <div
                                role="option"
                                aria-selected={!value}
                                className={`px-2 py-1 small fst-italic${activeIndex === 0 ? " bg-primary text-white" : ""}`}
                                style={{cursor: "pointer"}}
                                onMouseEnter={() => setActiveIndex(0)}
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={() => uebernehmenWert(null)}
                            >
                                – keine –
                            </div>
                        )}
                        {optionen.length === 0 && <div className="px-2 py-1 small text-muted">Keine passende Woche</div>}
                        {optionen.map((w, index) => {
                            const gesamtIndex = index + (ersterEintragIstLeer ? 1 : 0);
                            return (
                                <div
                                    key={w.wert}
                                    role="option"
                                    aria-selected={w.wert === value}
                                    className={`px-2 py-1 small d-flex align-items-center justify-content-between gap-2${gesamtIndex === activeIndex ? " bg-primary text-white" : ""}`}
                                    style={{cursor: "pointer"}}
                                    onMouseEnter={() => setActiveIndex(gesamtIndex)}
                                    onMouseDown={(e) => e.preventDefault()}
                                    onClick={() => uebernehmenWert(w.wert)}
                                >
                                    <span>{kwLabel(w.wert)}</span>
                                    {w.istAktuelleWoche && <span className="badge text-bg-secondary">aktuell</span>}
                                </div>
                            );
                        })}
                    </div>
                )}
            </Overlay>
        </>
    );
}
